'use client';

import Image from 'next/image';
import { useState } from 'react';
import { FEATURE_TABS } from './constants';
import SectionBadge from './SectionBadge';
import SectionCTA from './SectionCTA';
import MobileFeatureAccordion from './MobileFeatureAccordion';

export default function FeaturesSection() {
  const [activeTab, setActiveTab] = useState('save-time');
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);

  return (
    <section className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <noscript>
          <style>{`.feature-tab-panel { display: flex !important; }`}</style>
        </noscript>
        <SectionBadge label="Powerful Agency Tools" />

        <h2 className="text-[28px] lg:text-[42px] leading-tight mb-4 max-w-[655px] mx-auto pt-4">
          Everything Your Agency Needs to Work Faster{' '}
          <span className="text-[#7c7c7c]">and Look Pro</span>
        </h2>

        <p className="text-lg font-semibold text-gray-700 max-w-xl mx-auto">
        Save time, increase profit, and deliver a fully branded client experience with one platform.
        </p>

        {/* Feature Tabs */}
        <div className="hidden lg:flex justify-center gap-0 bg-[#F4F9FF] rounded-[108.333px] border-[0.8px] border-[#0070F3] p-1.5 max-w-[450px] mx-auto mt-12 mb-16" role="tablist" aria-label="Agency features">
          {FEATURE_TABS.map((tab) => (
            <button
              key={tab.id}
              id={`feature-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`feature-panel-${tab.id}`}
              onClick={() => {
                setActiveTab(tab.id);
                setActiveFeatureIndex(0);
              }}
              className={`px-6 py-2.5 rounded-full text-base font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#0070f3] text-white shadow-lg'
                  : 'text-[#0070f3]/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Feature Content - Desktop */}
        <div className="hidden lg:grid grid-cols-2 gap-8 mx-auto">
          {/* Left Side - Feature List */}
          <div className="relative flex gap-6 w-full h-[595px]">
            {/* Vertical Indicator */}
            <div className="relative flex-shrink-0 h-full w-[4px]">
              {/* Active Indicator */}
              <div 
                className="absolute left-0 w-[4px] h-[120px] rounded-[10px] bg-[#0070F3] transition-all duration-300"
                style={{ 
                  top: (() => {
                    const totalItems = FEATURE_TABS.find((tab) => tab.id === activeTab)?.items.length || 4;
                    const containerHeight = 595;
                    const indicatorHeight = 120;
                    const itemSpacing = (containerHeight - indicatorHeight) / (totalItems - 1);
                    return `${activeFeatureIndex * itemSpacing}px`;
                  })()
                }}
              />
            </div>
            
            {/* Content — every tab stays in the HTML for crawlers */}
            <div className="flex-1 relative">
              {FEATURE_TABS.map((tab) => {
                const selected = activeTab === tab.id
                return (
                  <div
                    key={tab.id}
                    className="feature-tab-panel flex h-full flex-col justify-between"
                    role="tabpanel"
                    id={`feature-panel-${tab.id}`}
                    aria-labelledby={`feature-tab-${tab.id}`}
                    style={selected ? undefined : { display: 'none' }}
                  >
                    {tab.items.map((item, index) => (
                      <div
                        key={item.title}
                        onClick={() => setActiveFeatureIndex(index)}
                        className={`text-left pb-4 cursor-pointer transition-opacity ${
                          index < 3 ? 'border-b border-gray-200' : ''
                        } ${activeFeatureIndex === index ? '' : 'opacity-40'}`}
                      >
                        <div className="flex items-end justify-between gap-4 mb-3">
                          <h3 className="text-2xl font-medium">{item.title}</h3>
                          {item.learnMoreHref && (
                            <a
                              href={item.learnMoreHref}
                              onClick={(e) => e.stopPropagation()}
                              className="text-[#0070f3] inline-flex items-center gap-1 text-sm font-medium hover:underline flex-shrink-0"
                            >
                              Learn more
                              <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                                <path d="M6 0L12 6L6 12" />
                              </svg>
                            </a>
                          )}
                        </div>
                        <p className="text-[#505050] mb-4">{item.description}</p>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right Side - Image */}
          <div className="relative w-full">
            <div className="sticky top-32">
              <div className="relative h-[595px] w-full rounded-[34px] overflow-hidden bg-gray-100">
                {(() => {
                  const currentTab = FEATURE_TABS.find((tab) => tab.id === activeTab);
                  const activeItem = currentTab?.items[activeFeatureIndex];
                  const imageSrc = activeItem?.image || '/images/features/features-project.webp';
                  return (
                    <Image 
                      src={imageSrc} 
                      alt={activeItem?.title || "App Screenshot"} 
                      fill 
                      className="object-cover" 
                    />
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Feature Content - Mobile */}
        <div className="lg:hidden space-y-12 mt-12">
          <MobileFeatureAccordion
            title="Save Time"
            subtitle="Streamline your workflow with tools that keep you organized"
            items={FEATURE_TABS[0].items}
            featureImage="/images/features/features-project.webp"
          />
          <MobileFeatureAccordion
            title="Grow Revenue"
            subtitle="Grow your revenue with features that scale your business"
            items={FEATURE_TABS[1].items}
            featureImage="/images/features/features-project.webp"
          />
          <MobileFeatureAccordion
            title="Impress Clients"
            subtitle="Impress clients with a polished, professional experience"
            items={FEATURE_TABS[2].items}
            featureImage="/images/features/features-project.webp"
          />
        </div>

        <SectionCTA href="/agency" label="Start For Free" />
      </div>
    </section>
  );
}

